import { inject, Provider, signal } from '@angular/core';
import { WA_CSS } from '@ng-web-apis/common';
import { WA_IS_WEBKIT } from '@ng-web-apis/platform';
import {
  TUI_ASSETS_PATH,
  TUI_DARK_MODE,
  TUI_ICON_RESOLVER,
} from '@taiga-ui/core';
import { TUI_ANCHOR_SUPPORT } from '@taiga-ui/core/tokens';

/**
 * Префиксы проектных иконок → каталог в `assets/icons/svg`.
 * Всё остальное (`@tui.*`) резолвится стандартным путём ассетов Taiga.
 */
const X_ICON_PREFIXES: ReadonlyArray<readonly [string, string]> = [
  ['x-icon-goods::', 'assets/icons/svg/request-goods'],
  ['x-icon-lp::', 'assets/icons/svg/product-types'],
  ['x-icon::', 'assets/icons/svg'],
];

export function provideXIconResolver(): Provider {
  return {
    provide: TUI_ICON_RESOLVER,
    useFactory: () => {
      const taigaAssetsPath = inject(TUI_ASSETS_PATH);

      return (icon: string): string => {
        for (const [prefix, dir] of X_ICON_PREFIXES) {
          if (icon.startsWith(prefix)) {
            return `${dir}/${icon.slice(prefix.length)}.svg`;
          }
        }

        return `${taigaAssetsPath}/${icon.replace(/@[a-z]+\./i, '').replaceAll('.', '/')}.svg`;
      };
    },
  };
}

/**
 * Тёмная тема управляется нашим `ThemeService` (настройки аккаунта),
 * поэтому отключаем встроенное в Taiga следование системной теме и
 * localStorage — значение выставляет `AppComponent`.
 */
export function provideAppDarkMode(): Provider {
  return {
    provide: TUI_DARK_MODE,
    useFactory: () => {
      const darkMode = signal(false);

      return Object.assign(darkMode, { reset: () => darkMode.set(false) });
    },
  };
}

/**
 * Taiga 5.28 позиционирует dropdown/hint через CSS anchor positioning
 * (`position-anchor` + `position-try-fallbacks`), если браузер его поддерживает.
 * В WebKit (Safari, в т.ч. все браузеры на iOS) эта связка уходит в бесконечный
 * цикл пересчёта стилей/раскладки внутри одного кадра — на стейдже страница
 * распределения на iPhone зависала на десятки секунд при работе с выпадающими
 * списками (запись Safari Timeline: один кадр 57 с, 100k+ layout без JS).
 * В WebKit отключаем anchor-режим — Taiga возвращается к JS-позиционированию.
 */
export function provideWebkitAnchorFallback(): Provider {
  return {
    provide: TUI_ANCHOR_SUPPORT,
    useFactory: (): boolean =>
      !inject(WA_IS_WEBKIT) &&
      !!inject(WA_CSS)?.supports('anchor-name', '--my-anchor'),
  };
}
